/**
 * Shared required-field labels + validation copy for website Add/Edit drawers.
 * Keep stars and error phrasing consistent across inspector surfaces.
 */

import React from "react";
import { COMPONENT_TITLE_REQUIRED_ERROR } from "./websiteComponentTitle";

/** Append " *" when required — safe to call with labels that already include it. */
export function requiredFieldLabel(
  label: string,
  required: boolean = true,
): string {
  const base = String(label || "").trim();
  if (!base || !required) return base;
  if (/\*$/.test(base)) return base;
  return `${base} *`;
}

/**
 * Render a field label with an accent/error asterisk when the string ends in "*".
 * Matches Banner Panel Redesign (`.req` accent; invalid → error).
 */
export function renderFieldLabel(label: React.ReactNode): React.ReactNode {
  if (typeof label !== "string") return label;
  const trimmed = label.trimEnd();
  const star = trimmed.match(/^(.*?)(\s*\*)$/);
  if (!star) return label;
  const base = star[1].trimEnd();
  if (!base) return label;
  return React.createElement(
    React.Fragment,
    null,
    base,
    " ",
    React.createElement(
      "span",
      { className: "mte-we-req", "aria-hidden": "true" },
      "*",
    ),
  );
}

export { COMPONENT_TITLE_REQUIRED_ERROR };

export const LINK_REQUIRED_ERROR = "A link is required.";
export const URL_REQUIRED_ERROR = "A URL is required.";
export const URL_INVALID_ERROR = "Please enter a valid URL.";
export const EMBED_CODE_REQUIRED_ERROR = "Embed code is required.";
export const BUTTON_LABEL_REQUIRED_ERROR = "A button label is required.";
export const BUTTON_TEXT_REQUIRED_ERROR = "Button text is required.";
export const CAMPAIGN_SELECT_REQUIRED_ERROR = "Please select a campaign.";
export const CAMPAIGN_NAME_REQUIRED_ERROR = "Campaign name is required.";
export const IMAGE_UPLOAD_REQUIRED_ERROR = "Please upload an image first.";
export const LINK_TITLE_REQUIRED_ERROR = "Link title is required.";
export const SECTION_TITLE_REQUIRED_ERROR = COMPONENT_TITLE_REQUIRED_ERROR;
export const BLOG_HEADER_EMPTY_ERROR = "Blog header is empty";
/** Default JournalGrid / Blog section heading so first Publish isn't blocked. */
export const DEFAULT_JOURNAL_GRID_HEADING = "Blog";

/**
 * Footer footnote under Add/Update when required fields are empty
 * (Banner Panel Redesign mock).
 */
function footnoteArticle(name: string): string {
  const n = String(name || "").trim();
  // Mass nouns / phrases that shouldn't take a/an.
  if (
    /^(nested content|tab content|column content|body text)$/i.test(n)
  ) {
    return "";
  }
  return /^[aeiou]/i.test(n) ? "an" : "a";
}

export function formatRequiredFieldsFootnote(names: string[]): string {
  const clean = names.map((n) => String(n || "").trim()).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length === 1) {
    const article = footnoteArticle(clean[0]);
    return `Add ${article ? `${article} ` : ""}${clean[0]} to finish`;
  }
  return `${clean.length} required fields left: ${clean.join(", ")}`;
}

/**
 * Map schema seed / text-media under-field errors to `data-mte-req-field` keys
 * for footer footnote + invalid paint (do not default CTA/link misses to "body text").
 * May return multiple keys when one seed message covers several fields.
 */
export function reqFieldKeysFromSeedError(message: string): string[] {
  const err = String(message || "").toLowerCase();
  if (!err) return [];
  if (/button label/.test(err)) return ["button label"];
  if (/button link|button url/.test(err)) return ["button link"];
  const ribbonMsg = err.match(
    /(?:add a message|ribbon message)(?: for message (\d+))?/,
  );
  if (ribbonMsg) {
    return [ribbonMsg[1] ? `message ${ribbonMsg[1]}` : "message"];
  }
  const ribbonUrl = err.match(
    /(?:add a link url|enter a valid link url)(?: for message (\d+))?/,
  );
  if (ribbonUrl) {
    return [ribbonUrl[1] ? `link url ${ribbonUrl[1]}` : "link url"];
  }
  if (/valid expiry date|expiry date \(mm\/dd\/yyyy\)/.test(err)) {
    return ["expiry"];
  }
  if (/brand/.test(err) && (/code/.test(err) || /expiry|expires/.test(err))) {
    return ["brand", "code", "description", "expiry"];
  }
  if (/follower count/.test(err)) return ["platform", "count"];
  if (/add a testimonial/.test(err)) return ["name", "quote"];
  if (/at least one question/.test(err)) return ["question"];
  if (/answer for every question|add an answer/.test(err)) return ["answer"];
  if (/embed/.test(err)) return ["embed code"];
  if (/quote/.test(err)) return ["quote"];
  if (/image|upload/.test(err)) return ["image"];
  if (/section headline|headline/.test(err)) return ["headline"];
  if (/section title/.test(err)) return ["title"];
  if (/video/.test(err)) return ["video url"];
  if (/question/.test(err)) return ["question"];
  if (/publication name/.test(err)) return ["name"];
  if (/map address|address/.test(err)) return ["address"];
  if (/body text|text content|default text/.test(err)) return ["body text"];
  if (/required content/.test(err)) return ["required content"];
  return ["body text"];
}

/** Paint/clear `data-mte-req-field` invalid state for Add/Update footnote flow. */
export function paintInspectorRequiredFields(
  root: HTMLElement | null,
  names: string[],
) {
  if (!root) return;
  const want = new Set(
    names.map((n) => n.trim().toLowerCase()).filter(Boolean),
  );
  root.querySelectorAll<HTMLElement>("[data-mte-req-field]").forEach((el) => {
    const key = String(el.getAttribute("data-mte-req-field") || "")
      .trim()
      .toLowerCase();
    const bad = Boolean(key && want.has(key));
    el.classList.toggle("is-invalid", bad);
    el.querySelectorAll<HTMLElement>("input, textarea, select").forEach((c) => {
      if (bad) c.setAttribute("aria-invalid", "true");
      else c.removeAttribute("aria-invalid");
    });
  });
}

export function clearInspectorRequiredField(
  root: HTMLElement | null,
  fieldEl: HTMLElement | null,
) {
  if (!root || !fieldEl) return;
  const wrap = fieldEl.closest<HTMLElement>("[data-mte-req-field]");
  if (!wrap || !wrap.classList.contains("is-invalid")) return;
  wrap.classList.remove("is-invalid");
  wrap.querySelectorAll<HTMLElement>("input, textarea, select").forEach((c) => {
    c.removeAttribute("aria-invalid");
  });
}

export function scrollInspectorToFieldError(root: HTMLElement | null) {
  if (!root || typeof window === "undefined") return;
  window.setTimeout(() => {
    const el = root.querySelector<HTMLElement>(
      "[data-mte-req-field].is-invalid, .mte-we-field.is-invalid, .mte-we-insp-image.is-invalid, .mte-we-field-hint--error[role='alert'], .mte-we-field-hint--error, [data-mte-publish-error='true']",
    );
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const focusable = el?.querySelector<HTMLElement>(
      "input:not([type='hidden']):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), label.mte-we-photo-upload",
    );
    window.setTimeout(() => {
      focusable?.focus?.({ preventScroll: true });
    }, 280);
  }, 0);
}

/** Shared `data-mte-req-field` keys for SchemaBlock RepeatEditor rows. */
export function repeatEditorReqField(
  kind: string,
  fieldId: string,
  index: number,
  listLen: number,
): string | undefined {
  const indexed = listLen > 1;
  const n = index + 1;
  if (kind === "ribbon") {
    if (fieldId === "message") return indexed ? `message ${n}` : "message";
    if (fieldId === "url") return indexed ? `link url ${n}` : "link url";
    return undefined;
  }
  const byKind: Record<string, Record<string, string>> = {
    accordion: { q: "question", a: "answer" },
    discount: {
      brand: "brand",
      code: "code",
      desc: "description",
      expires: "expiry",
    },
    testimonials: { name: "name", quote: "quote" },
    followers: { platform: "platform", count: "count" },
    asseenon: { name: "name" },
  };
  return byKind[kind]?.[fieldId];
}
